import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const Partner = () => {
  useEffect(() => {
    document.title = "For Doctors - Hyperapt";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Hyperapt for Doctors - First in-clinic pharmacy and consultation platform in India");
    else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = "Hyperapt for Doctors - First in-clinic pharmacy and consultation platform in India";
      document.head.appendChild(meta);
    }
    window.scrollTo(0, 0);
  }, []);

  const brands = [
    "Cipla", "Sun Pharma", "Dr. Reddy's", "Mankind", "Lupin",
    "Torrent", "Alkem", "Zydus", "Intas", "Glenmark", "Abbott", "Macleods"
  ];

  const testimonials = [
    {
      name: "Dr. V Bhanushali, MS General Surgery",
      quote: "Hyperapt completely changed how my clinic operates. The HealthMap gives me instant insights, and patients are thrilled to get medicines right away.",
    },
    {
      name: "Dr. Jayesh, MS Orthopedics",
      quote: "No inventory, no hassle. I just write the prescription digitally, and Hyperapt handles the rest. It's the ultimate upgrade for any modern practice.",
    },
    {
      name: "Dr. Sarang Apte, MBBS DNB",
      quote: "My patient retention has skyrocketed. The convenience of a few minute in-clinic pharmacy delivery is something no one else in my city is offering.",
    }
  ];

  return (
    <div className="min-h-screen bg-web-bg text-web-text font-web-sans flex flex-col selection:bg-web-primary selection:text-white overflow-x-hidden">
      {/* Header - Sleek, centered */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md transition-all duration-300 h-20 flex items-center border-b border-web-border/50">
        <div className="max-w-7xl mx-auto px-4 w-full flex items-center justify-center">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto" />
            <span className="font-web-serif font-semibold text-[36px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>
        </div>
      </header>

      <main className="flex-1 w-full relative pt-20">

        {/* Decorative Background Blob */}
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-web-secondary/20 rounded-full blur-[120px] -z-10 pointer-events-none" />
        <div className="absolute top-1/3 left-0 w-[800px] h-[800px] bg-web-primary/5 rounded-full blur-[150px] -z-10 pointer-events-none" />

        {/* Hero Section */}
        <section className="relative w-full max-w-7xl mx-auto px-4 py-20 lg:py-32 flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-full lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left z-10"
          >
            <div className="inline-block px-4 py-1.5 rounded-full bg-web-primary/10 text-web-primary font-semibold text-sm tracking-wide mb-6">
              FIRST TIME EVER TECHNOLOGY
            </div>
            <h1 className="font-web-serif text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Smarter Consultations with Pharmacy Built In. <span className="text-web-secondary-dark italic font-normal">— Digitally.</span>
            </h1>
            <p className="text-xl text-web-text/70 leading-relaxed max-w-xl font-light mb-10">
              Hyperapt is the only platform in India that integrates powerful consultation management with lightning-fast in-clinic medicine delivery in minutes. Zero physical setup required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Link to="/smartclinic" className="px-8 py-4 rounded-full bg-web-primary text-white font-medium shadow-lg shadow-web-primary/30 hover:shadow-web-primary/50 transition-all text-center">
                Create Account
              </Link>
              <a href="#cta-section" className="px-8 py-4 rounded-full bg-white border border-web-border text-web-text font-medium hover:bg-web-bg transition-all text-center">
                Explore Solutions
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="w-full lg:w-1/2 relative"
          >
            <div className="relative aspect-[4/3] w-full rounded-3xl overflow-hidden shadow-2xl border border-white/50">
              {/* Image Placeholder */}
              <div className="absolute inset-0 bg-[#EFEBE1] flex items-center justify-center">
                <span className="text-web-secondary-dark/50 font-web-serif italic">Doctor consulting a patient</span>
              </div>
              <img src="/website_assets/partner_hero.png" alt="Doctor Consultation" className="w-full h-full object-cover relative z-10 opacity-0 transition-opacity duration-500" onLoad={(e) => e.target.style.opacity = 1} onError={(e) => e.target.style.display = 'none'} />
            </div>
          </motion.div>
        </section>

        {/* The Problem / Our Solution Story */}
        <section className="py-24 bg-white relative z-20 border-y border-web-border">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="font-web-serif text-4xl lg:text-5xl font-bold mb-8 text-web-text"
            >
              The old way is <span className="text-web-primary italic">broken.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.2 }}
              className="text-xl text-web-text/70 leading-relaxed font-light"
            >
              Patients wait for hours in waiting rooms, only to walk out and search for another pharmacy to fill their prescriptions. Doctors rely on fragmented software that doesn't talk to each other. <strong className="font-semibold text-web-text">Hyperapt connects it all.</strong> You write the prescription digitally, and our system immediately dispatches genuine medicines directly to your clinic desk before the patient even leaves the premises.
            </motion.p>
          </div>
        </section>

        {/* Features Showcase */}
        <section className="py-24 lg:py-32 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 flex flex-col gap-32">

            {/* Feature 1: HealthMap */}
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                className="w-full lg:w-1/2 order-2 lg:order-1"
              >
                <div className="relative aspect-square w-full max-w-md mx-auto lg:max-w-none rounded-[40px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white">
                  <div className="absolute inset-0 bg-[#EFEBE1] flex items-center justify-center p-8 text-center">
                    <span className="text-web-secondary-dark/50 font-web-serif italic">Tablet displaying app</span>
                  </div>
                  <img src="/website_assets/feature_healthmap.png" alt="HealthMap Feature" className="w-full h-full object-cover relative z-10 opacity-0 transition-opacity duration-500" onLoad={(e) => e.target.style.opacity = 1} onError={(e) => e.target.style.display = 'none'} />
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                className="w-full lg:w-1/2 order-1 lg:order-2 text-center lg:text-left"
              >
                <h2 className="font-web-serif text-4xl lg:text-5xl font-bold mb-6">Consultation Management & <span className="text-web-primary">HealthMap</span></h2>
                <p className="text-xl text-web-text/70 leading-relaxed font-light mb-8">
                  Enhance the quality and speed of your consultations. Our intelligent HealthMap gives you deep insights into patient history instantly, while seamlessly generating beautifully formatted digital prescriptions and GST receipts.
                </p>
                <ul className="space-y-4 text-left inline-block lg:block">
                  {["Instant Patient History & Insights", "Automated Digital Prescriptions", "Instantly WhatsApped to Patients"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-web-text/80 font-medium">
                      <div className="w-6 h-6 rounded-full bg-web-primary/10 flex items-center justify-center text-web-primary">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Feature 2: In-Clinic In-Minutes Pharmacy */}
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                className="w-full lg:w-1/2 text-center lg:text-left"
              >
                <h2 className="font-web-serif text-4xl lg:text-5xl font-bold mb-6">In-Clinic In-Minutes <span className="text-web-primary">Pharmacy</span></h2>
                <p className="text-xl text-web-text/70 leading-relaxed font-light mb-8">
                  No physical inventory needed. Generate a prescription on Hyperapt, and we deliver genuine, discounted medicines straight to your clinic desk while the patient is still resting on your couch.
                </p>
                <ul className="space-y-4 text-left inline-block lg:block">
                  {["Zero Physical Inventory Setup", "In-Minutes Hyper-Delivery", "Direct-to-Home Delivery Option"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-web-text/80 font-medium">
                      <div className="w-6 h-6 rounded-full bg-web-primary/10 flex items-center justify-center text-web-primary">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                className="w-full lg:w-1/2"
              >
                <div className="relative aspect-square w-full max-w-md mx-auto lg:max-w-none rounded-[40px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-white">
                  <div className="absolute inset-0 bg-[#EFEBE1] flex items-center justify-center p-8 text-center">
                    <span className="text-web-secondary-dark/50 font-web-serif italic">hyperapt delivery personnel</span>
                  </div>
                  <img src="/website_assets/feature_pharmacy.png" alt="In-Minutes Pharmacy" className="w-full h-full object-cover relative z-10 opacity-0 transition-opacity duration-500" onLoad={(e) => e.target.style.opacity = 1} onError={(e) => e.target.style.display = 'none'} />
                </div>
              </motion.div>
            </div>

          </div>
        </section>

        {/* Loved by All Section */}
        <section className="py-24 bg-white border-t border-web-border">
          <div className="max-w-5xl mx-auto px-4 flex flex-col gap-16 lg:gap-24">

            {/* Doctors */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="flex flex-col md:flex-row gap-4 md:gap-16 items-start md:items-center"
            >
              <h2 className="font-web-serif text-4xl lg:text-5xl font-bold text-web-text w-full md:w-1/2 tracking-tight">
                Doctors <span className="text-[#FF7F6A] italic">love</span> Hyperapt
              </h2>
              <div className="w-full md:w-1/2">
                <p className="text-lg text-web-text/70 leading-relaxed font-medium">
                  The new standard in care. Seamless patient experience. Zero physical inventory management. Intuitive health tracking.
                </p>
              </div>
            </motion.div>

            {/* Staffs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="flex flex-col md:flex-row gap-4 md:gap-16 items-start md:items-center"
            >
              <h2 className="font-web-serif text-4xl lg:text-5xl font-bold text-web-text w-full md:w-1/2 tracking-tight">
                Staffs <span className="text-[#F1C40F] italic">love</span> Hyperapt
              </h2>
              <div className="w-full md:w-1/2">
                <p className="text-lg text-web-text/70 leading-relaxed font-medium">
                  Already in your workflow. Dedicated phone lines. Automated GST receipts. No more messy paperwork.
                </p>
              </div>
            </motion.div>

            {/* Patients */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="flex flex-col md:flex-row gap-4 md:gap-16 items-start md:items-center"
            >
              <h2 className="font-web-serif text-4xl lg:text-5xl font-bold text-web-text w-full md:w-1/2 tracking-tight">
                Patients <span className="text-[#789A9B] italic">love</span> Hyperapt
              </h2>
              <div className="w-full md:w-1/2">
                <p className="text-lg text-web-text/70 leading-relaxed font-medium">
                  The best pharmacy. Delivered inside the clinic in minutes. No extra costs. Discounted genuine medicines.
                </p>
              </div>
            </motion.div>

          </div>
        </section>

        {/* Authentic Brands Marquee */}
        <section className="py-20 bg-white border-y border-web-border overflow-hidden relative">
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          <div className="text-center mb-10">
            <h3 className="font-web-serif text-2xl font-bold text-web-text/80">Every Genuine Brand Available.</h3>
            <p className="text-web-text/50 text-sm mt-2">100% Authentic Medicines & Products. No Compromise.</p>
          </div>

          <div className="flex w-[200%] md:w-max">
            <motion.div
              animate={{ x: [0, -1000] }}
              transition={{ repeat: Infinity, duration: 30, ease: "linear" }}
              className="flex items-center gap-16 md:gap-24 px-8"
            >
              {[...brands, ...brands, ...brands].map((brand, i) => (
                <span key={i} className="font-web-sans font-bold text-3xl md:text-5xl text-web-text/10 uppercase tracking-widest whitespace-nowrap">
                  {brand}
                </span>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 lg:py-32 px-4 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-web-serif text-4xl lg:text-5xl font-bold mb-4 text-web-text">Trusted by Top Doctors</h2>
            <p className="text-xl text-web-text/70 font-light">Join the revolution in clinic management.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="bg-white/60 backdrop-blur-xl p-8 rounded-3xl border border-white shadow-xl shadow-black/5 flex flex-col h-full"
              >
                <div className="text-web-primary mb-6">
                  <svg className="w-10 h-10 opacity-50" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <p className="font-web-serif text-lg text-web-text/80 leading-relaxed italic mb-8 flex-1">
                  "{t.quote}"
                </p>
                <div>
                  <h4 className="font-bold text-web-text">{t.name}</h4>
                  <div className="flex gap-1 text-yellow-400 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Interactive CTA Block */}
        <section id="cta-section" className="py-24 bg-web-secondary/10 border-t border-web-border relative overflow-hidden">
          <div className="absolute -top-[300px] -right-[300px] w-[800px] h-[800px] bg-white rounded-full blur-[100px] -z-10 pointer-events-none opacity-50" />

          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="font-web-serif text-4xl lg:text-5xl font-bold mb-4 text-web-text">Ready to elevate your practice?</h2>
              <p className="text-xl text-web-text/70 font-light">Choose how you'd like to get started with Hyperapt.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Card 1: Meeting */}
              <motion.a
                href="mailto:partner@hyperapt.com?subject=Schedule%20a%20Clinic%20Meeting"
                whileHover={{ y: -10 }}
                className="group bg-white p-8 rounded-3xl border border-web-border shadow-lg hover:shadow-2xl hover:border-web-primary/30 transition-all cursor-pointer flex flex-col items-center text-center h-full"
              >
                <div className="w-16 h-16 rounded-2xl bg-web-bg flex items-center justify-center mb-6 group-hover:bg-web-primary/10 transition-colors">
                  <svg className="w-8 h-8 text-web-text group-hover:text-web-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
                <h3 className="font-web-serif text-2xl font-bold mb-3 text-web-text group-hover:text-web-primary transition-colors">Schedule Meeting</h3>
                <p className="text-web-text/70 font-light mb-8 flex-1">Let our team visit your clinic to demonstrate the platform in person.</p>
                <span className="font-semibold text-web-primary flex items-center gap-2">Book Now <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg></span>
              </motion.a>

              {/* Card 2: Create Account (Primary) */}
              <Link
                to="/smartclinic"
                className="group bg-web-primary p-8 rounded-3xl shadow-xl shadow-web-primary/20 hover:shadow-2xl hover:shadow-web-primary/40 transition-all cursor-pointer flex flex-col items-center text-center h-full relative overflow-hidden text-left w-full block hover:no-underline hover:-translate-y-2.5 duration-300"
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity" />
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mb-6 mx-auto">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                </div>
                <h3 className="font-web-serif text-2xl font-bold mb-3 text-white text-center w-full">Create Account</h3>
                <p className="text-white/80 font-light mb-8 flex-1 text-center w-full">Set up your free account instantly and start generating prescriptions today.</p>
                <span className="font-semibold text-white flex items-center gap-2 mx-auto">Start Now <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg></span>
              </Link>

              {/* Card 3: Call Us */}
              <motion.a
                href="tel:9004581717"
                whileHover={{ y: -10 }}
                className="group bg-white p-8 rounded-3xl border border-web-border shadow-lg hover:shadow-2xl hover:border-web-primary/30 transition-all cursor-pointer flex flex-col items-center text-center h-full"
              >
                <div className="w-16 h-16 rounded-2xl bg-web-bg flex items-center justify-center mb-6 group-hover:bg-web-primary/10 transition-colors">
                  <svg className="w-8 h-8 text-web-text group-hover:text-web-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </div>
                <h3 className="font-web-serif text-2xl font-bold mb-3 text-web-text group-hover:text-web-primary transition-colors">Call Us</h3>
                <p className="text-web-text/70 font-light mb-8 flex-1">Have questions? Talk to our dedicated care team directly.</p>
                <span className="font-semibold text-web-primary flex items-center gap-2">Tap here to call <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg></span>
              </motion.a>
            </div>
          </div>
        </section>

      </main>

      {/* Footer - Sleek & Minimal */}
      <footer className="bg-web-bg text-web-text py-8 border-t border-web-border mt-auto relative z-30">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto opacity-90" />
            <span className="font-web-serif font-semibold text-[32px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>

          <nav className="flex flex-wrap justify-center gap-6 text-sm text-web-text/70 font-light">
            <Link to="/trackorder" className="hover:text-web-primary transition-colors">Track Order</Link>
            <Link to="/partner" className="hover:text-web-primary transition-colors">For Doctors</Link>
            <Link to="/terms" className="hover:text-web-primary transition-colors">Terms of Service</Link>
            <Link to="/privacypolicy" className="hover:text-web-primary transition-colors">Privacy Policy</Link>
            <Link to="/returnpolicy" className="hover:text-web-primary transition-colors">Return Policy</Link>
          </nav>

          <div className="text-sm text-web-text/50 font-light">
            © {new Date().getFullYear()} Hyperapt. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Partner;